import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer, Zoom } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css"
import logo from '../img/logo/hyundaimobis_CI.png'

import { UnauthenticatedTemplate } from "@azure/msal-react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../authConfig';
import { useAuth } from '../contexts/AuthContext';

function Login() {
  const { instance, accounts } = useMsal();
  const { isAuthenticated, isLoading, exchangeToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExchanging, setIsExchanging] = useState(false);

  // Get the redirect location from state or default to /overview
  const from = location.state?.from?.pathname || '/overview';

  // Redirect if already authenticated with internal JWT
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  // Handle MSAL account - if user has MSAL account but no internal JWT, exchange token
  useEffect(() => {
    const handleMsalAccount = async () => {
      const activeAccount = instance.getActiveAccount();

      // If MSAL account exists but we're not authenticated with internal JWT
      if (activeAccount && !isAuthenticated && !isLoading && !isExchanging) {
        setIsExchanging(true);
        try {
          await exchangeToken();
          navigate(from, { replace: true });
        } catch (error) {
          console.error('Token exchange error:', error);
          toast.error('Authentication failed. Please try again.', {
            position: toast.POSITION.TOP_CENTER,
          });
        } finally {
          setIsExchanging(false);
        }
      }
    };

    handleMsalAccount();
  }, [accounts, instance, isAuthenticated, isLoading, isExchanging, exchangeToken, navigate, from]);

  const handleRedirect = async () => {
    try {
      await instance.loginRedirect({
        ...loginRequest,
      });
    } catch (error) {
      console.error('Login redirect error:', error);
      toast.error('Failed to redirect to login. Please try again.', {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  // Show loading state during exchange
  if (isExchanging || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg">
        <div className="flex flex-col items-center bg-white p-8 rounded-md shadow-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-700">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer draggable={false} transition={Zoom} autoClose={3000} style={{ width: "100%", textAlign: "center", fontSize: "100%" }} hideProgressBar />
      <UnauthenticatedTemplate>
        <div className="min-h-[calc(100vh)] flex flex-col items-center justify-center login-bg" >
          <div className="flex flex-col bg-white shadow-md px-4 sm:px-6 md:px-8 lg:px-10 py-8 rounded-md w-full max-w-md">
            <img src={logo} alt="Logo" />
            <div className="relative mt-10 h-px bg-gray-300" />
            <div className="mt-5">
              <div className="flex w-full">
                <button type="button"
                  className="flex items-center justify-center focus:outline-none text-white text-sm sm:text-base bg-blue-600 hover:bg-blue-700 rounded py-2 w-full transition duration-150 ease-in" onClick={handleRedirect}>
                  <span className="mr-2 uppercase">Sign In</span>
                  <span>
                    <svg className="h-6 w-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  )
}

export default Login;
